// University of New Mexico: public college page (#2744), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UNM's Common Data Set 2025-2026 (cds_25_26_updated.pdf), linked from
//    the Office of Institutional Analytics' Common Data Set page: C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 (no early decision, no
//    early action; read from the PDF's form fields because the printed
//    check boxes are ambiguous in the text dump).
//  - UNM Admissions: Admissions Process (freshmen) and Admission
//    Requirements. Neither page carries a year; they are the pages as posted
//    on the read date. The fall priority application date is June 1; UNM
//    publishes no decision dates.
//  - UNM Financial Aid, Estimated Cost of Attendance 2026-2027 (main campus
//    undergraduate living on campus, New Mexico resident and non-resident).
//    Quirk: the CDS G1 lists $11,585 in-state tuition AND $1,639.80 in
//    required fees, but $11,585 is the aid office's combined "Tuition &
//    Fees" line (the bursar's Fall 2026 rate sheet puts tuition plus
//    mandatory and flat fees near that figure), so the CDS would count fees
//    twice. The page uses the aid office's items. Its non-resident total
//    ($54,371) is $1 below the sum of its own items ($54,372); the items
//    are used.
//  - College Scorecard, school id 187985 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UNM Common Data Set 2025-2026',
        url: 'https://oia.unm.edu/facts-and-figures/common-data-set.html',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UNM Admissions, Admissions Process for Freshmen',
        url: 'https://admissions.unm.edu/future-students/freshmen/index.html',
        read: '2026-10-01',
    },
    requirements: {
        label: 'UNM Admissions, Admission Requirements',
        url: 'https://admissions.unm.edu/future-students/freshmen/admission-requirements.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'UNM Financial Aid, Estimated Cost of Attendance 2026-2027',
        url: 'https://finaid.unm.edu/coa/26-27/index.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?187985-University-of-New-Mexico-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-new-mexico',
    kbId: 'university_of_new_mexico',
    name: 'University of New Mexico',
    shortName: 'UNM',
    aliases: ['UNM'],
    location: 'Albuquerque, NM',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UNM brand site, brand.unm.edu); decoration only.
    branding: {
        primary: '#BA0C2F',
        secondary: '#A7A8AA',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_new_mexico_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 13589,
        admitted: 10866,
        enrolled: 3775,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 6870, admitted: 5608 },
            { group: 'Out-of-state', applied: 5939, admitted: 5032 },
            { group: 'International', applied: 780, admitted: 226 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UNM\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UNM has no early decision or early action plan. Its June 1 date for fall is a priority application date, not an early plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Fall priority application date', value: 'June 1' },
        rows: [
            { plan: 'Fall priority application date', deadline: 'June 1', decision: null, binding: false },
        ],
        note: 'A complete application is the application, official high school transcripts (and college transcripts if you have any) and the $25 fee or an approved waiver; essays, portfolios and recommendations are not needed. UNM reviews applications once they are complete and returns decisions as quickly as possible, but does not publish decision dates. Some admitted freshmen are required to start in the summer after high school graduation.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UNM does not require the ACT, SAT or CLT to be considered for admission, though it says scores may help in some cases. If you send them, they should come from the testing service, but scores on your high school transcript are accepted. UNM uses your highest ACT composite or highest SAT total, and does not use the SAT writing section.',
        source: 'requirements',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [11585, 34734] },
            { item: 'Food and housing (on campus)', amounts: [12471, 12471] },
            { item: 'Books and supplies', amounts: [1869, 1869] },
            { item: 'Transportation', amounts: [2498, 2498] },
            { item: 'Miscellaneous', amounts: [2800, 2800] },
        ],
        totals: [31223, 54372],
        note: 'For an undergraduate on the main campus living on campus, fall and spring, at 15 semester hours a term. UNM calls these estimates; course fees, lab fees and tuition differentials for some programs can add to them.',
        source: 'costs',
        netPrice: {
            average: 19966,
            byIncome: [
                { income: '$0 to $30,000', amount: 19896 },
                { income: '$30,001 to $48,000', amount: 17600 },
                { income: '$48,001 to $75,000', amount: 19104 },
                { income: '$75,001 to $110,000', amount: null },
                { income: 'Over $110,000', amount: 23804 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $75,001 to $110,000 band. Your own price comes from UNM\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
